import BaseMapController from "./base_map_controller"
import { BaseMarker, MapBounds } from "@/utils/map_interfaces"
import { initializeMarkerClusterer } from "@/utils/map_utils"
import { Turbo } from "@hotwired/turbo-rails"

interface SearchMarker extends BaseMarker {
  info: {
    result_count: number;
    categories: Array<{ id: number, name: string, uid: string }>;
    location: string;
    created_at: string;
  };
}

type CategoryStyle = [string, string]
interface CategoryData { [key: string]: CategoryStyle }

const DEFAULT_CATEGORY_STYLE: CategoryStyle = ['All Categories', '#444444']

export default class extends BaseMapController {
  static values = {
    ...BaseMapController.values,
    categories: { type: Object }
  }

  declare readonly categoriesValue: CategoryData

  protected async fetchMarkers(bounds?: MapBounds) {
    const url = this.addFiltersToUrl(new URL(this.markersUrlValue, window.location.origin), bounds)

    const response = await fetch(url)
    const markers: SearchMarker[] = await response.json()

    this.clearMarkers()
    this.renderMarkers(markers)
  }

  protected addFiltersToUrl(url: URL, bounds: MapBounds = this.getBounds()) {
    if (bounds) {
      Object.entries(bounds).forEach(([key, value]) => {
        url.searchParams.set(key, value.toString())
      })
    }

    const currentParams = new URLSearchParams(window.location.search)

    // Preserve search-specific filters
    for (const param of ['category', 'timeframe']) {
      const value = currentParams.get(param)
      if (value) url.searchParams.set(param, value)
    }

    return url
  }

  protected createMarkerIcon(markerData: BaseMarker): google.maps.Icon {
    const searchMarker = markerData as SearchMarker;
    if (!searchMarker.info?.categories?.length) {
      return this.createDefaultIcon()
    }
    return this.createCategoryIcon(searchMarker.info.categories)
  }

  protected createDefaultIcon(): google.maps.Icon {
    return {
      url: this.createSvgUrl(`
        <circle cx="7.5" cy="7.5" r="6.75" fill="${DEFAULT_CATEGORY_STYLE[1]}" fill-opacity="0.6"/>
      `),
      scaledSize: new google.maps.Size(10, 10),
      anchor: new google.maps.Point(5, 5)
    }
  }

  private createCategoryIcon(categories: SearchMarker['info']['categories']): google.maps.Icon {
    const styles = categories.map(category => this.getCategoryStyle(category.uid))
    const svgPaths = this.createCategoryPaths(styles)

    return {
      url: this.createSvgUrl(svgPaths),
      scaledSize: new google.maps.Size(12, 12),
      anchor: new google.maps.Point(6, 6)
    }
  }

  private createCategoryPaths(styles: CategoryStyle[]): string {
    return styles.map((style, i) => {
      const path = this.createCategoryPath(i, styles.length)
      return `<path d="${path}" fill="${style[1]}" fill-opacity="0.9"/>`
    }).join('')
  }

  private createCategoryPath(index: number, total: number): string {
    const radius = 6.75
    const anglePerSection = 360 / total
    const startAngle = (index * anglePerSection) * (Math.PI / 180)
    const endAngle = ((index + 1) * anglePerSection) * (Math.PI / 180)

    const x1 = 7.5 + radius * Math.cos(startAngle)
    const y1 = 7.5 + radius * Math.sin(startAngle)
    const x2 = 7.5 + radius * Math.cos(endAngle)
    const y2 = 7.5 + radius * Math.sin(endAngle)

    const largeArcFlag = total === 1 ? 1 : 0

    return `M 7.5 7.5 L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`
  }

  protected handleMarkerClick<T extends BaseMarker>(markerData: T) {
    return () => Turbo.visit(`/searches/${markerData.id}`, { frame: 'drawer_frame' })
  }

  protected getCategoryStyle(category: string | undefined): CategoryStyle {
    if (!category) return DEFAULT_CATEGORY_STYLE
    return this.categoriesValue[category] || DEFAULT_CATEGORY_STYLE
  }

  selectCategory(event: Event) {
    event.preventDefault()
    const link = event.currentTarget as HTMLAnchorElement
    const category = new URL(link.href).searchParams.get('category')
    this.updateUrlHistory('category', category)
    this.fetchMarkersForCurrentBounds()
  }

  updateTimeframe(event: Event) {
    const select = event.target as HTMLSelectElement
    this.updateUrlHistory('timeframe', select.value)
    this.fetchMarkersForCurrentBounds()
  }

  protected updateUrlHistory(param: 'category' | 'timeframe', value: string | null) {
    const currentUrl = new URL(window.location.href)
    if (value) {
      currentUrl.searchParams.set(param, value)
    } else {
      currentUrl.searchParams.delete(param)
    }
    window.history.replaceState({}, '', currentUrl.toString())
  }

  // Method to pass current map bounds to analytics dashboard
  attachFiltersToURL(event: Event) {
    const link = event.currentTarget as HTMLAnchorElement
    const url = this.addFiltersToUrl(new URL(link.href))

    link.href = url.toString()
  }
}
